import React from 'react';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../modules/page-container/page-container';
import { useCartQuery } from '../../modules/cart/queries';
import { GridContainer, GridItem } from '../../visual-components/grid/grid';
import { CartTable } from '../../modules/cart/components/cart-table';
import { Stepper } from '../../visual-components/stepper/stepper';
import { Spacer } from '../../visual-components/spacer/spacer';
import { InlineNotification } from '../../visual-components/inline-notification/inline-notification';
import { ButtonLink } from '../../visual-components/button/button';
import { usePathBuilder } from '../../core/routing/hooks/use-path-builder';
import { ProcessButtonLayout } from '../../visual-components/process-button-layout/process-button-layout';
import { useCustomerQuery } from '../../modules/customer/queries';
import { CartDetails } from '../../modules/cart/components/cart-details';
import { Link } from '../../visual-components/link/link';
import { FullPageLoader } from '../../visual-components/full-page-loader/full-page-loader';
import { TableCellDeleteItem } from '../../modules/cart/components/table-cell-delete-item';

export const Overview: React.FC = () => {
  const { t } = useTranslation();
  const { cartFinalizePath, homePath } = usePathBuilder();
  const { data: cart, isLoading: isLoadingCart, error: cartError } = useCartQuery();

  const { data: customer, isLoading: isLoadingCustomer, error: customerError } = useCustomerQuery();
  const customerData = customer?.data;

  const hasItemsInCart = (cart?.data?.items.length ?? 0) > 0;
  const canContinue = !isLoadingCart && !isLoadingCustomer && hasItemsInCart;

  if (isLoadingCart) {
    return <FullPageLoader />;
  }

  return (
    <PageContainer documentTitle={t('cart.documentTitle')}>
      <GridContainer>
        <GridItem>
          <Stepper
            currentStep={1}
            steps={[
              {
                label: t('cart.stepper.cart'),
              },
              { label: t('cart.stepper.finalize') },
              {
                label: t('cart.stepper.confirm'),
              },
            ]}
          />
        </GridItem>
        <GridItem>
          <Spacer size="09" />
        </GridItem>
        {hasItemsInCart ? (
          <GridItem>
            <InlineNotification
              text={t('cart.preliminaryHint.text')}
              title={t('cart.preliminaryHint.title')}
              type="info"
            />
            <Spacer size="06" />
            <CartTable
              data={cart?.data}
              error={cartError}
              isLoading={isLoadingCart}
              renderDeleteItemCell={(props) => <TableCellDeleteItem {...props} />}
            />
            <Spacer size="08" />
          </GridItem>
        ) : (
          <GridItem>
            <InlineNotification title={t('cart.emptyCartDescription')} type="info" />
          </GridItem>
        )}
        <GridItem>
          <Spacer size="08" />
        </GridItem>
        {hasItemsInCart ? (
          <GridItem span="6">
            <CartDetails customerData={customerData} error={customerError} isLoading={isLoadingCustomer} />
          </GridItem>
        ) : null}
        <GridItem>
          <ProcessButtonLayout
            left={
              <Link icon="arrow-left" iconPosition="left" to={homePath()}>
                {t('global.continueShopping')}
              </Link>
            }
            right={
              <ButtonLink
                disabled={!canContinue}
                icon="arrow-right"
                iconPosition="right"
                to={cartFinalizePath()}
                variant="primary"
              >
                {t('pagination.continue')}
              </ButtonLink>
            }
          />
        </GridItem>
      </GridContainer>
    </PageContainer>
  );
};
